const TIME_ZONE = 'Europe/Madrid'

const parts = new Intl.DateTimeFormat('en-GB', {
  timeZone: TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
})

interface LocalParts {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
}

const toLocalParts = (ms: number): LocalParts => {
  const get = (type: Intl.DateTimeFormatPartTypes): number => Number(parts.formatToParts(ms).find(part => part.type === type)?.value)
  return { year: get('year'), month: get('month'), day: get('day'), hour: get('hour'), minute: get('minute'), second: get('second') }
}

const pad = (n: number): string => n.toString().padStart(2, '0')

export const formatDateTime = (iso: string): string => {
  const ms = Date.parse(iso)
  if (Number.isNaN(ms)) return ''
  const p = toLocalParts(ms)
  return `${pad(p.day)}/${pad(p.month)}/${String(p.year)} ${pad(p.hour)}:${pad(p.minute)}`
}

export const formatShortDateTime = (iso: string): string => {
  const ms = Date.parse(iso)
  if (Number.isNaN(ms)) return ''
  const p = toLocalParts(ms)
  return `${pad(p.day)}/${pad(p.month)} ${pad(p.hour)}:${pad(p.minute)}`
}

export const barcelonaYear = (ms: number): number => toLocalParts(ms).year

const LOCAL_INPUT = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/

export const toDateTimeInput = (iso: unknown): string => {
  if (typeof iso !== 'string' || iso === '') return ''
  if (LOCAL_INPUT.test(iso)) return iso.slice(0, 16)
  const ms = Date.parse(iso)
  if (Number.isNaN(ms)) return ''
  const p = toLocalParts(ms)
  return `${String(p.year)}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`
}

export const fromDateTimeInput = (value: unknown): string | null => {
  if (typeof value !== 'string') return null
  const match = LOCAL_INPUT.exec(value)
  if (!match) return null
  const groups: (string | undefined)[] = match.slice(1)
  const [year = 0, month = 0, day = 0, hour = 0, minute = 0, second = 0] = groups.map(n => (n === undefined ? 0 : Number(n)))
  const wanted = Date.UTC(year, month - 1, day, hour, minute, second)
  let ms = wanted
  for (let i = 0; i < 2; i++) {
    const p = toLocalParts(ms)
    ms += wanted - Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second)
  }
  const p = toLocalParts(ms)
  const roundTrips = p.year === year && p.month === month && p.day === day && p.hour === hour && p.minute === minute
  return roundTrips ? new Date(ms).toISOString() : null
}

const HOUR = 3_600_000

export const barcelonaMidnights = (from: number, to: number): number[] => {
  const midnights: number[] = []
  for (let at = Math.ceil(from / HOUR) * HOUR; at <= to; at += HOUR) {
    const p = toLocalParts(at)
    if (p.hour === 0 && p.minute === 0) midnights.push(at)
  }
  return midnights
}

export const formatDay = (ms: number): string => {
  const p = toLocalParts(ms)
  return `${pad(p.day)}/${pad(p.month)}`
}
